import { type ComputedRef, computed } from 'vue'
import { useChatStore } from '@/modules/chat-matrix-fast/stores/chatViewStore'
import { useChatStore as useChatStoreCore } from '@/modules/chat-matrix-fast/stores/chatStore'
import { useNotifications } from './useNotifications'
import { useReactionGrouping } from './useReactionGrouping'
import { getReactionEmoji, stripEmojiKeyPrefix } from '../utils/reactionUtils'
import { emojiToReactionTypeId } from '../services/matrixReactionService'
import { findMessageIndex, getMessageIdentityKey } from '../utils/messageIdentity'
import type { MessageWithReply } from '../types/messageTypes'
import type { ChatMessage } from '../types'

export interface ChatLogReactionsOptions {
  /** UI-timeline активного чата (useActiveChatMessages). */
  messages: ComputedRef<ChatMessage[]>
  /** Профиль текущего пользователя для подписи моих реакций. */
  reactionProfile: ComputedRef<{ fullName?: string; avatar?: string | null } | null | undefined>
}

/**
 * Реакции ChatLog поверх core-store: выбор/применение/нормализация
 * и группировка для отображения.
 */
export function useChatLogReactions(options: ChatLogReactionsOptions) {
  const { messages, reactionProfile } = options

  const store = useChatStore()
  const chatStoreCore = useChatStoreCore()
  const { showError } = useNotifications()

  const DEFAULT_REACTION_TYPES = [
    { id: 1, name: 'Like', icon: '👍' },
    { id: 2, name: 'Love', icon: '❤️' },
    { id: 3, name: 'Laugh', icon: '😂' },
    { id: 4, name: 'Wow', icon: '😮' },
    { id: 5, name: 'Sad', icon: '😢' },
    { id: 6, name: 'Angry', icon: '😠' },
  ]

  const availableReactions = computed(() => {
    const types = chatStoreCore.reactionTypes || []
    return types.length > 0 ? types : DEFAULT_REACTION_TYPES
  })

  const chatMembers = computed(() => {
    const membersFromCurrentChat = chatStoreCore.currentChat?.members
    const membersFromActiveChat = store.activeChat?.chat?.members

    return membersFromActiveChat || membersFromCurrentChat
  })

  const { groupReactions, getMyReaction, getMyReactionTypeId }
    = useReactionGrouping(
      computed(() => chatStoreCore.currentUserId),
      availableReactions,
      chatMembers,
      messages,
      reactionProfile,
    )

  const resolveStoreMessageId = (messageId: string | number): number | string | null => {
    const coreIndex = findMessageIndex(chatStoreCore.messages, {
      id: messageId,
      matrixEventId: typeof messageId === 'string' ? messageId : undefined,
    })
    if (coreIndex >= 0) {
      return getMessageIdentityKey(chatStoreCore.messages[coreIndex])
    }

    const uiMessage = messages.value.find(
      (m: any) => String(m.id) === String(messageId)
        || String(m.matrix_event_id) === String(messageId),
    )
    if (uiMessage?.matrix_event_id) {
      return uiMessage.matrix_event_id
    }
    if (uiMessage?.id != null) {
      return uiMessage.id
    }

    return messageId
  }

  const normalizeReactionTypeId = (
    reactionTypeId: string | null,
    message?: MessageWithReply,
  ): string | null => {
    if (!reactionTypeId) {
      return null
    }

    if (reactionTypeId.startsWith('emoji:')) {
      const emoji = stripEmojiKeyPrefix(reactionTypeId)
      return String(emojiToReactionTypeId(emoji, availableReactions.value))
    }

    const numericId = Number(reactionTypeId)
    if (Number.isFinite(numericId) && numericId >= 10000 && message) {
      const myReaction = getMyReaction(message)
      const emojiKey = stripEmojiKeyPrefix((myReaction as any)?.matrix_emoji_key)
      if (emojiKey) {
        return String(emojiToReactionTypeId(emojiKey, availableReactions.value))
      }
    }

    return String(reactionTypeId)
  }

  const applyReactionChange = async (
    messageId: string | number,
    reactionTypeId: number | string,
    emojiOverride?: string,
  ) => {
    const storeMessageId = resolveStoreMessageId(messageId)
    if (storeMessageId == null) {
      return
    }

    const messageIndex = findMessageIndex(chatStoreCore.messages, {
      id: storeMessageId,
      matrixEventId: typeof messageId === 'string' ? messageId : undefined,
    })
    const message = messageIndex >= 0 ? chatStoreCore.messages[messageIndex] : undefined
    const uiMessage = messages.value.find(
      (m: any) => String(m.id) === String(messageId)
        || String(m.id) === String(storeMessageId)
        || String(m.matrix_event_id) === String(messageId),
    ) as MessageWithReply | undefined
    const matrixEventIdHint = message?.matrix_event_id || uiMessage?.matrix_event_id
    const sourceMessage = message || uiMessage

    const myReaction = sourceMessage ? getMyReaction(sourceMessage) : null
    const currentEmoji = stripEmojiKeyPrefix(
      (myReaction as any)?.matrix_emoji_key
      || (myReaction ? getReactionEmoji(myReaction as any) : null)
      || '',
    ) || null

    const currentReactionTypeId = sourceMessage ? getMyReactionTypeId(sourceMessage) : null
    const current = normalizeReactionTypeId(currentReactionTypeId, sourceMessage ?? undefined)
    const next = normalizeReactionTypeId(
      emojiOverride ? `emoji:${stripEmojiKeyPrefix(emojiOverride)}` : String(reactionTypeId),
      sourceMessage ?? undefined,
    )
    const selectedReaction = availableReactions.value.find(
      reaction => String(reaction.id) === next,
    )
    const nextEmoji = stripEmojiKeyPrefix(
      emojiOverride
      || (selectedReaction ? getReactionEmoji(selectedReaction) : null)
      || (typeof reactionTypeId === 'string' && !Number.isFinite(Number(reactionTypeId))
        ? String(reactionTypeId)
        : '')
      || '',
    ) || null

    const resolvedId = emojiOverride
      ? emojiToReactionTypeId(stripEmojiKeyPrefix(emojiOverride), availableReactions.value)
      : reactionTypeId

    const sameById = Boolean(current && current === next)
    const sameByEmoji = Boolean(
      currentEmoji
      && nextEmoji
      && currentEmoji.replace(/\uFE0F/g, '').trim() === nextEmoji.replace(/\uFE0F/g, '').trim(),
    )

    if (sameById || sameByEmoji) {
      await chatStoreCore.clearMyReactions(storeMessageId, matrixEventIdHint)
    } else {
      await chatStoreCore.setExclusiveReaction(
        storeMessageId,
        resolvedId,
        nextEmoji || undefined,
        matrixEventIdHint,
      )
    }
  }

  const getGroupedReactions = (msgData: MessageWithReply) => {
    return groupReactions(msgData.reactions || [], msgData)
  }

  const handleReactionClick = async (
    messageId: string | number,
    reactionTypeId: number | string,
    isMyReaction = false,
  ) => {
    try {
      if (isMyReaction) {
        const storeMessageId = resolveStoreMessageId(messageId)
        if (storeMessageId == null) {
          return
        }

        const messageIndex = findMessageIndex(chatStoreCore.messages, {
          id: storeMessageId,
          matrixEventId: typeof messageId === 'string' ? messageId : undefined,
        })
        const message = messageIndex >= 0 ? chatStoreCore.messages[messageIndex] : undefined
        const uiMessage = messages.value.find(
          (m: any) => String(m.id) === String(messageId)
            || String(m.id) === String(storeMessageId)
            || String(m.matrix_event_id) === String(messageId),
        ) as MessageWithReply | undefined

        await chatStoreCore.clearMyReactions(
          storeMessageId,
          message?.matrix_event_id || uiMessage?.matrix_event_id,
        )
        return
      }

      await applyReactionChange(messageId, reactionTypeId)
    } catch (error) {
      showError(error, 'Toggle reaction')
    }
  }

  /** Emoji моей реакции на сообщении (для подсветки в пикере). */
  const getActiveEmoji = (messageId: string | number): string | null => {
    const messageIndex = findMessageIndex(chatStoreCore.messages, {
      id: messageId,
      matrixEventId: typeof messageId === 'string' ? messageId : undefined,
    })
    const message = messageIndex >= 0 ? chatStoreCore.messages[messageIndex] : undefined
    if (!message) {
      return null
    }
    const my = getMyReaction(message)
    return stripEmojiKeyPrefix(
      (my as any)?.matrix_emoji_key
      || (my ? getReactionEmoji(my as any) : null)
      || '',
    ) || null
  }

  return {
    availableReactions,
    getGroupedReactions,
    resolveStoreMessageId,
    applyReactionChange,
    handleReactionClick,
    getActiveEmoji,
  }
}
